import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from '@plato/ui/containers/popover';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import { FilterBars } from '../../assets/icons/filter-bars.tsx';
import type { UseControlledStateProps } from '../../lib/hooks/use-controlled-state.ts';
import { useControlledState } from '../../lib/hooks/use-controlled-state.ts';
import { useMemoDeepEqualValue } from '../../lib/hooks/use-memo-deep-equal.ts';
import { CancellableSearchBox } from '../search/cancellable-search-box.tsx';
import { Button } from '../ui/button.tsx';
import { AccordionFilters } from './accordion-filters.tsx';
import type { AnyFilter, VerticalFilterOption } from './types/filter-types.ts';
import type { UiFilterVisibility } from './types/ui-filter-visibility.ts';
import type { VerticalFilterViewState } from './types/vertical-filter-view-state.ts';
import { hasActiveFilters, transformFiltersForAccordion } from './utils/filter-transform-utils.ts';
import { VerticalFiltersFooter } from './vertical-filters-footer.tsx';
import { VerticalFiltersHeader, type ViewFormData } from './vertical-filters-header.tsx';

type OnlyStringFilterKeys<T extends AnyFilter> = keyof {
    [K in keyof T]: NonNullable<T[K]> extends string ? T[K] : never;
};

// Re-export ViewFormData for convenience
export type { ViewFormData };

export type EditingFilterView = {
    id: string;
    name: string;
    visibility: UiFilterVisibility;
};

export type VerticalFiltersProps<TFilter extends AnyFilter> = {
    appliedFilters: TFilter;
    onApplyFilters: (filters: TFilter) => void;
    onFiltersChange?: Dispatch<SetStateAction<TFilter>>;
    searchFilterKey?: OnlyStringFilterKeys<TFilter>;
    searchPlaceholder?: string;
    itemCount?: number;
    isLoading: boolean;
    canShareFilterViews?: boolean;
    defaultCurrency: string;
    // Filter configuration
    filters: (VerticalFilterOption<TFilter> | undefined)[];
    onClearAll?: () => void;
    onSaveView?: (name: string, visibility: UiFilterVisibility, currentFilters: TFilter) => void;
    onUpdateView?: (
        viewId: string,
        name: string,
        visibility: UiFilterVisibility,
        currentFilters: TFilter,
    ) => void;
    // Optional viewKey identifier for saved views (UiFilterView.viewKey)
    viewKey?: string;
    // AppliedFilters configuration
    defaultFilters: TFilter;
    // Edit mode
    editingFilterView?: EditingFilterView;
    // Controlled open state
    open?: UseControlledStateProps<boolean>['state'];
    setOpen?: UseControlledStateProps<boolean>['setState'];
    // Content rendered next to the filter button (e.g., a Select dropdown)
    rightSlot?: React.ReactNode;
} & React.PropsWithChildren;

export function VerticalFilters<TFilter extends AnyFilter>({
    appliedFilters,
    onApplyFilters,
    onFiltersChange,
    searchFilterKey,
    searchPlaceholder,
    itemCount,
    isLoading,
    canShareFilterViews = false,
    defaultCurrency,
    filters: filtersInput,
    onClearAll,
    onSaveView,
    onUpdateView,
    viewKey,
    defaultFilters,
    editingFilterView,
    ...props
}: VerticalFiltersProps<TFilter>) {
    const { t } = useTranslation();

    // A checkbox filter offering fewer than two options is a dead accordion row: zero options opens
    // into nothing, one option narrows nothing. Hide both once the options query has settled.
    // A single-option filter the user already applied is hidden too, but its chip in
    // VerticalAppliedFilters stays visible and still clears it.
    const filters = filtersInput.filter(
        (filter): filter is VerticalFilterOption<TFilter> =>
            filter !== undefined && (isLoading || filter.type !== 'checkbox' || filter.options.length > 1),
    );
    const memoizedAppliedFilters = useMemoDeepEqualValue(appliedFilters);
    const [internalFilters, setInternalFilters] = useState<TFilter>(appliedFilters);
    const [open, setOpen] = useControlledState({
        state: props.open,
        setState: props.setOpen,
        defaultState: false,
    });
    const anchorRef = useRef<HTMLDivElement>(null);
    const [viewState, setViewState] = useState<VerticalFilterViewState>('default');
    const [viewFormData, setViewFormData] = useState<ViewFormData>({
        name: '',
        visibility: 'only_me',
    });
    const [isFormValid, setIsFormValid] = useState(true);

    // Transform filters and calculate if any have active values
    const transformedFilters = transformFiltersForAccordion(filters, internalFilters);
    const hasActiveFiltersValue = hasActiveFilters(transformedFilters);

    useEffect(() => {
        if (editingFilterView) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- legacy code
            setViewFormData({
                id: editingFilterView.id,
                name: editingFilterView.name,
                visibility: editingFilterView.visibility,
            });
            setViewState('edit-view');
        }
    }, [editingFilterView, setOpen]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- legacy code
        setInternalFilters(appliedFilters);
    }, [memoizedAppliedFilters]);

    useDebounce(
        () => {
            onFiltersChange?.(internalFilters);
        },
        300,
        [internalFilters],
    );

    const handleSearch = (searchBoxText: string) => {
        if (searchFilterKey) {
            onApplyFilters({
                ...appliedFilters,
                [searchFilterKey]: searchBoxText,
            });
        }
    };

    const setFilters = (newFilters: SetStateAction<TFilter>) => {
        setInternalFilters(newFilters);
    };

    const setOpenAndResetFilters = (newOpen: boolean) => {
        // We want to mannualy control the open state if we're editing a view
        if (!editingFilterView) {
            setOpen(newOpen);

            // Reset edit state when closing
            if (!newOpen) {
                setViewFormData({
                    name: '',
                    visibility: 'only_me',
                });
                setViewState('default');
            }
        }

        setFilters(appliedFilters);
    };

    const filterContent = (
        <div className={'flex h-full w-full flex-col overflow-y-hidden md:h-[75dvh] md:w-96'}>
            {filters.length > 0 && (
                <VerticalFiltersHeader
                    state={viewState}
                    setState={setViewState}
                    showSaveViewButton={!!viewKey}
                    canShareFilterViews={canShareFilterViews}
                    hasActiveFilters={hasActiveFiltersValue}
                    onClearAll={() => {
                        setFilters(defaultFilters);

                        // Call the flattened API's onClearAll if provided
                        if (onClearAll) {
                            onClearAll();
                        }
                    }}
                    viewFormData={viewFormData}
                    onViewFormDataChange={setViewFormData}
                    onValidationChange={setIsFormValid}
                />
            )}
            <div className="flex w-full grow flex-row overflow-y-hidden">
                {/* Render filters if provided */}
                {filters.length > 0 && (
                    <AccordionFilters
                        className="border-none w-full overflow-y-auto"
                        defaultCurrency={defaultCurrency}
                        filters={transformedFilters}
                        onFilterChange={(filterId, value) => {
                            const updatedFilters = {
                                ...internalFilters,
                                [filterId]: value,
                            };
                            setFilters(updatedFilters);
                        }}
                    />
                )}
            </div>
            <VerticalFiltersFooter
                state={viewState}
                hasActiveFilters={hasActiveFiltersValue}
                onClearAll={() => {
                    setViewState('default');
                    setViewFormData({
                        name: '',
                        visibility: 'only_me',
                    });
                    setOpen(false);
                }}
                onSaveView={() => {
                    if (viewFormData.name.trim() && isFormValid) {
                        if (viewState === 'edit-view' && viewFormData.id && onUpdateView) {
                            onUpdateView(
                                viewFormData.id,
                                viewFormData.name,
                                viewFormData.visibility,
                                internalFilters,
                            );
                        } else if (viewState === 'save-view' && onSaveView) {
                            onSaveView(viewFormData.name, viewFormData.visibility, internalFilters);
                        }
                        setViewState('default');
                        setViewFormData({
                            name: '',
                            visibility: 'only_me',
                        });
                    }
                }}
                onApplyFilters={() => {
                    onApplyFilters(internalFilters);
                    setOpen(false);
                }}
                itemCount={itemCount}
                isLoading={isLoading}
                isFormValid={isFormValid}
            />
        </div>
    );

    const triggerButton = (
        <Button
            variant="outline"
            selected={open}
            className="flex items-center justify-center"
            icon={FilterBars}
        >
            <span>{t('filter_button__text', { ns: 'filters' })}</span>
        </Button>
    );

    return (
        <Popover open={open} onOpenChange={setOpenAndResetFilters}>
            <div ref={anchorRef} className="flex flex-row gap-3 grow md:grow-0 flex-wrap">
                <div className="flex grow md:flex-none">
                    <CancellableSearchBox
                        placeholder={searchPlaceholder ?? t('search')}
                        value={searchFilterKey ? (appliedFilters[searchFilterKey] as string) : ''}
                        onChange={handleSearch}
                        className="w-full"
                        aria-label={t('filter_search__aria_label', { ns: 'filters' })}
                    />
                </div>
                <div className="flex flex-1 gap-3">
                    <PopoverTrigger render={triggerButton} />
                    {props.rightSlot}
                </div>
            </div>
            <PopoverContent anchor={anchorRef} align="start" className="w-full p-0">
                {/* The panel has no visible title, but the mobile sheet needs a name. */}
                <PopoverTitle className="sr-only">{t('filter_modal__title', { ns: 'filters' })}</PopoverTitle>
                {filterContent}
            </PopoverContent>
        </Popover>
    );
}
